import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { faCalendarAlt } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFormikContext } from 'formik';
import type {
  ChangeEvent,
  CSSProperties,
  FC,
  InputHTMLAttributes,
  ReactNode,
} from 'react';
import { useCallback, useEffect } from 'react';
import type { InputTime } from 'src/components/form/datepicker/utils';
import {
  dateRegExp,
  dateTimeRegExp,
  dateTimeSeparator,
  extractDateFromString,
  getMask,
  getPlaceholder,
  toISOString,
} from 'src/components/form/datepicker/utils';
import { useFieldGroup } from 'src/components/form/field-group';
import { InnerAddon } from 'src/components/form/inner-addon';
import { getInputClasses, SimpleInput } from 'src/components/form/input';
import {
  InputMask,
  type BeforeMaskedStateChangeStates,
  type InputState,
} from 'src/components/form/input-mask';
import { RemoveButton } from 'src/components/form/select/components/remove-button';
import { useFocusManager } from 'src/components/form/use-focus-manager';
import { Tooltip } from 'src/components/overlay/tooltip';
import type { DropdownState } from 'src/hooks/use-dropdown';
import { cn } from 'src/utils/cn';

type DateInputProps = {
  time: InputTime;
  name: string;
  value: string;
  setUserValue: (value: string) => void;
  setOpen: (active: boolean) => void;
  fieldId: string;
  showError: boolean;
  disabled?: boolean;
  disabledMessage?: ReactNode;
  dropdownState?: DropdownState;
  validate: (value: string | null) => string | undefined;
  onTab?: () => void;
  isoValue: string | null;
  showFocusStyle: boolean;
} & InputHTMLAttributes<HTMLInputElement>;

type InputStateWithEnteredString = InputState & {
  enteredString: string;
};

function stateHasEnteredString(
  inputMask: InputState
): inputMask is InputStateWithEnteredString {
  return (inputMask as any)?.enteredString;
}

export const DateInputField: FC<DateInputProps> = ({
  time,
  name,
  value,
  setUserValue: setValue,
  setOpen,
  fieldId,
  showError,
  disabled,
  disabledMessage,
  dropdownState,
  validate,
  onTab,
  isoValue,
  showFocusStyle,
  ...inputProps
}) => {
  const { setFieldValue, setFieldError, setFieldTouched } =
    useFormikContext<unknown>();

  const formGroup = useFieldGroup();
  useEffect(() => {
    if (formGroup) {
      formGroup.onError({ name, showError });
    }
  }, [showError, name, formGroup]);

  const focusRef = useFocusManager<HTMLInputElement>();

  // a fix for when users try to enter dates that are not first of month via e.g. copy & paste
  // in that case, we extract the date from the input and mask it manually
  // otherwise, entering e.g. 15.09.2020 would turn mask into 01.50.9202 due to mask shifting text
  // right in a desperate attempt to match it.
  const beforeMaskStateChangedHandler =
    time === 'gas-month' || time === 'gas-quarter' || time === 'gas-year'
      ? (evstates: BeforeMaskedStateChangeStates) => {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (evstates.nextState && stateHasEnteredString(evstates.nextState)) {
            const input = evstates.nextState.enteredString;
            const datePart = extractDateFromString(input);
            if (datePart) {
              const firstDayOfMonth = `01${datePart.substring(2)}${dateTimeSeparator}06:00`;
              evstates.nextState.value = firstDayOfMonth;
            }
          }
          return evstates.nextState;
        }
      : undefined;

  const showRemoveButton = Boolean(value) && !disabled;

  const mask = getMask(time);
  const placeholder = getPlaceholder(time);
  const displayedText = value ? value : placeholder;

  // needed to "calculate" the offset of the "at" text
  const hiddenDateText = displayedText.split(dateTimeSeparator)[0];

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      // note: InputMask also triggers change events on focus and blur
      if (event.type === 'focus' || event.type === 'blur') return;

      setValue(event.currentTarget.value);
      // flag field as touched even though we haven't changed the _real_
      // value (behind `setFieldValue`), but the the mask value (behind `setValue`)
      setFieldTouched(name, true, false);

      const enteredPlaceholder =
        event.currentTarget.value === getPlaceholder(time);

      if (!validate(event.currentTarget.value) && !enteredPlaceholder) {
        // user entered a valid date
        setFieldError(name, undefined);
        setFieldValue(name, toISOString(event.currentTarget.value), true);
      } else {
        // validate in case it is the placeholder, to trigger potential required validation
        setFieldValue(name, null, enteredPlaceholder);
        const fullInput = event.currentTarget.value.match(
          time ? dateTimeRegExp : dateRegExp
        );
        if (fullInput) {
          // user entered a "full", but invalid date
          setFieldError(name, 'Please enter a valid date for {label}.');
        } else {
          // user entered a partial date or nothing
          setFieldError(name, undefined);
        }
      }
    },
    [
      name,
      setFieldTouched,
      setFieldValue,
      setValue,
      time,
      validate,
      setFieldError,
    ]
  );

  return (
    <InnerAddon
      showError={showError}
      disabled={disabled}
      left={
        <FontAwesomeIcon
          icon={faCalendarAlt}
          aria-hidden="true"
          className="text-body-sm!"
        />
      }
      right={
        showRemoveButton && (
          <RemoveButton
            error={showError}
            disabled={disabled}
            data-testid={`${name}-remove`}
            onClick={() => setFieldValue(name, null)}
            className="pointer-events-auto h-[calc(100%-4px)]!" // 4px for focus outline
          >
            <FontAwesomeIcon icon={faTimes} />
          </RemoveButton>
        )
      }
    >
      {({ leftBounds, rightBounds }) => (
        <div
          ref={dropdownState?.refs.setReference}
          {...dropdownState?.getReferenceProps()}
        >
          {time && (
            <span
              aria-hidden
              className={cn(
                `
                  pointer-events-none absolute grid grid-flow-col items-baseline
                  leading-[1.15] text-foreground-surface-subdued
                  legacy:text-legacy-brand-light-2
                `,
                showError &&
                  `
                    text-foreground-support-error
                    legacy:text-legacy-error
                  `,
                disabled &&
                  `
                    text-foreground-surface-disabled
                    legacy:text-legacy-inactive
                  `
              )}
            >
              {/* we re-use the input style to get the correct baseline and positioning */}
              <span
                style={
                  {
                    '--field-padding-left': leftBounds
                      ? `${leftBounds.width}px`
                      : undefined,
                  } as CSSProperties
                }
                className={getInputClasses({
                  error: showError,
                  className: cn(
                    'invisible',
                    // fake placeholder behaviour
                    !(value || focusRef.current === document.activeElement) &&
                      'italic',
                    // cut the right side of the input to make space for the "at" text
                    `
                      border-r-0 pr-0
                      legacy:pr-0
                    `,
                    leftBounds &&
                      `
                        pl-(--field-padding-left)
                        legacy:pl-(--field-padding-left)
                      `
                  ),
                })}
              >
                {hiddenDateText}&nbsp;
              </span>
              at
            </span>
          )}
          <Tooltip
            content={disabled && disabledMessage ? disabledMessage : undefined}
          >
            {(targetProps) => (
              <InputMask
                mask={mask}
                maskPlaceholder={placeholder}
                value={value}
                beforeMaskedStateChange={beforeMaskStateChangedHandler}
                onChange={handleChange}
                onKeyDown={(e) => {
                  if (e.key === 'Tab' && onTab) {
                    onTab();
                  }
                }}
                {...inputProps}
                onFocus={(event) => {
                  inputProps.onFocus?.(event);
                  setOpen(true);
                }}
                onBlur={(event) => {
                  inputProps.onBlur?.(event);
                  // for partially filled out inputs (e.g. time === "gas-month") we remove
                  // the value completely on blur when it matches the placeholder
                  if (getPlaceholder(time) === value) {
                    setValue('');
                  }
                }}
                disabled={disabled}
                {...targetProps}
              >
                <SimpleInput
                  ref={focusRef}
                  id={fieldId}
                  error={showError}
                  name={name}
                  placeholder={getPlaceholder(time)}
                  data-test-error={showError}
                  data-testid={name}
                  leftBounds={leftBounds}
                  rightBounds={rightBounds}
                  data-test-iso-value={isoValue}
                  className={cn(showFocusStyle && 'legacy:legacy-focus-style')}
                />
              </InputMask>
            )}
          </Tooltip>
        </div>
      )}
    </InnerAddon>
  );
};
